import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Form, Button, Card, Spinner, Container } from 'react-bootstrap';
import { FaArrowUp, FaDatabase } from 'react-icons/fa';
import ReactMarkdown from 'react-markdown';
import './Chat.css';

const API_URL = process.env.REACT_APP_API_URL;

function Chat() {
  const { token } = useContext(AuthContext);
  const location = useLocation();
  const [messages, setMessages] = useState(location.state?.messages || []);

  useEffect(() => {
    if (messages.length > 0) {
      localStorage.setItem('chatMessagesBackup', JSON.stringify(messages));
    }
  }, [messages]);

  useEffect(() => {
    // Only hydrate from backup if no messages in location.state
    if (messages.length === 0) {
      const backup = localStorage.getItem('chatMessagesBackup');
      if (backup) {
        try {
          const parsed = JSON.parse(backup);
          setMessages(parsed);
          //localStorage.removeItem('chatMessagesBackup'); // Clean up
        } catch (err) {
          console.error('Failed to parse chatMessagesBackup:', err);
        }
      }
    }
  }, []);

  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async () => {
    if (!query.trim()) return;

    const assistantCount = messages.filter((m) => m.role === 'assistant').length;
    if (!token && assistantCount >= 3) {
      toast.success('Please log in to continue chatting.');
      localStorage.setItem('chatMessagesBackup', JSON.stringify(messages));
      localStorage.setItem('postLoginRedirect', '/chat');

      // Delay navigation by 1 second
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    }

    setLoading(true);
  
    const userMessage = { role: 'user', content: query };
    const updatedMessages = [...messages, userMessage];
    const liveMessages = [...updatedMessages, { role: 'assistant', content: '' }];
    setMessages(liveMessages);
  
    try {
      const response = await fetch(
        `${API_URL}/chat`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'text/event-stream',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            query,
            /*history: messages,*/
          }),
        }
      );
  
      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let fullMessage = '';
      let partial = '';
  
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
  
        const chunk = decoder.decode(value, { stream: true });
        partial += chunk;
  
        const lines = partial.split('\n');
  
        for (let i = 0; i < lines.length - 1; i++) {
          const line = lines[i].trim();
          if (line.startsWith('data:')) {
            try {
              const json = JSON.parse(line.replace(/^data:\s*/, ''));
              if (json.content) {
                fullMessage += json.content;
  
                setMessages((prev) => {
                  const updated = [...prev];
                  updated[updated.length - 1].content = fullMessage;
                  return updated;
                });
                await new Promise((r) => setTimeout(r, 0));
              }
            } catch (err) {
              console.error('Error parsing stream chunk:', err);
            }
          }
        }
  
        partial = lines[lines.length - 1]; // preserve the last partial line
      }
    } catch (error) {
      console.error('Streaming error:', error);
      setMessages([...updatedMessages, { role: 'assistant', content: 'Error retrieving data.' }]);
      toast.error('Error retrieving response.');
    }
  
    setQuery('');
    setLoading(false);
  };  

  return (
    <Container>
      <div className={`chat-container ${messages.length === 0 ? 'centered-chat' : ''}`}>
        <ToastContainer />

        <div className="mb-2">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => {
              setMessages([]);
              localStorage.removeItem('chatMessagesBackup');
              toast.info('Chat history cleared.');
            }}
          >
            Clear Chat History
          </Button>
        </div>

        {/* Scrollable Chat Messages */}
        <Card className="chat-box">
          <Card.Body className="chat-messages">
            {messages.length === 0 ? (
              <div className="empty-chat-placeholder">
                <h3>Welcome! Ask anything about CRT modifications.</h3>
              </div>
            ) : (
              messages.map((msg, index) => (
                <div key={index} className={`chat-message ${msg.role}`}>
                  <strong>{msg.role === 'user' ? 'You' : 'Sunthar'}:</strong>{' '}
                  {msg.isLoading ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <ReactMarkdown
                      components={{
                        a: ({ node, ...props }) => (
                          <a {...props} target="_blank" rel="noopener noreferrer">
                            {props.children}
                          </a>
                        ),
                      }}
                      skipHtml={false}
                      breaks={true} // Ensures newlines (\n) from API responses are respected
                    >
                      {msg.content + (msg.role === 'assistant' && index === messages.length - 1 && loading ? ' █' : '')}
                    </ReactMarkdown>
                  )}
                </div>
              ))
            )}
            <div ref={messagesEndRef}></div>
          </Card.Body>
        </Card>

        {/* Sticky Chat Input Field */}
        <Form className="chat-input-container" onSubmit={(e) => { e.preventDefault(); sendMessage(); }}>
          <Form.Control
            type="text"
            placeholder="Ask about CRT modifications..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            disabled={loading}
            className="chat-input"
          />
          <Button className="send-btn" onClick={sendMessage} disabled={loading}>
            {loading ? <Spinner animation="border" size="sm" /> : <FaArrowUp />}
          </Button>
        </Form>
      </div>
    </Container>
  );
}

export default Chat;
